import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, EventEmitter, Output, inject } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { FormBuilder, FormGroup, Validators } from '@angular/forms'
import { TranslateService } from '@ngx-translate/core'
import { BookableArea, Booking, BookingBusyRange, BookingCandidate, BookingInvitee, BookingInviteePayload } from '@app/models/booking.model'
import { BookingsService } from '../services/bookings.service'
import { BookingAllowanceConfirmService } from '../services/booking-allowance-confirm.service'
import { BookingTimeService } from '../services/booking-time.service'

/**
 * Create a booking on someone's behalf, or move / retitle an existing one (YB-2073).
 *
 * Deliberately thin: PAPI owns every rule (slot alignment, opening hours, duration bounds, credit
 * balance, conflicts) and refuses with a machine-readable code, so this form validates only what
 * it can know locally — that the fields are filled and the end is after the start. Re-implementing
 * the rules here would only produce a second, drifting copy of them.
 *
 * On a `booking_conflict` PAPI returns the blocking ranges; they are shown inline so the admin can
 * pick another slot without a second round trip.
 */
@Component({
  selector: 'app-booking-form',
  templateUrl: './booking-form.component.html',
  styleUrls: ['./booking-form.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BookingFormComponent {
  private destroyRef = inject(DestroyRef)

  @Output() saved: EventEmitter<void> = new EventEmitter<void>()

  visible = false
  position = document.dir === 'rtl' ? 'left' : 'right'
  saving = false

  form: FormGroup
  booking: Booking | null = null
  areas: BookableArea[] = []

  /**
   * The Booked-by picker is an autocomplete, not a filtered select, because the matching happens
   * on the SERVER (person columns are encrypted, so only PAPI can compare a decrypted substring).
   * A p-select would re-filter whatever came back against its own option labels and quietly drop
   * every phone-number hit — an autocomplete treats the suggestions it is handed as final.
   */
  candidates: BookingCandidate[] = []
  candidatesLoading = false
  selectedCandidate: BookingCandidate | null = null
  /** The booker of an existing booking, shown read-only: PAPI's update accepts times and title only. */
  lockedBookerName = ''

  /** Populated from a `booking_conflict` refusal so the admin sees what is in the way. */
  busyRanges: BookingBusyRange[] = []

  /**
   * The invitee list being edited (YB-2085) — existing people and newly added ones in one array,
   * because PAPI takes the COMPLETE list, not a delta, and reconciles it by phone number.
   */
  invitees: BookingInvitee[] = []

  /**
   * Whether the admin actually touched the list.
   *
   * The distinction is load-bearing on an edit: `undefined` means "leave the list alone" and `[]`
   * means "remove everyone". Sending the list unconditionally would make a retitle re-send a list
   * the admin never looked at — harmless while it round-trips exactly, and a mass revocation the
   * moment anything about it is imperfect. Same rule the title already follows.
   */
  private inviteesTouched = false

  /** The add-a-person row. Separate from `form` so its validity never blocks the booking's save. */
  inviteeForm: FormGroup

  /** Set when the typed number is already on the list — PAPI would silently collapse the duplicate. */
  inviteeDuplicate = false

  /**
   * UTC instant → the client's wall clock, for the template.
   *
   * A method rather than a precomputed field because the only thing that needs it is the handful
   * of conflicting ranges on a refused save — at most a few rows, rendered once.
   */
  wall(instant: string): Date {
    return this.bookingTime.toWall(instant)
  }

  constructor(
    private fb: FormBuilder,
    private bookingsService: BookingsService,
    private allowanceConfirm: BookingAllowanceConfirmService,
    public bookingTime: BookingTimeService,
    public translate: TranslateService,
    private cdr: ChangeDetectorRef
  ) {
    this.form = this.buildForm()
    this.inviteeForm = this.buildInviteeForm()
  }

  get isNew(): boolean {
    return !this.booking
  }

  /**
   * The room picker, grouped by branch when the client has more than one.
   *
   * Room names are only unique within a branch, so a flat client-wide list offers several entries
   * reading "Room B" and the admin picks one by luck. Grouping is the smallest thing that makes the
   * choice a real choice; a single-branch client gets the plain list and no empty group header.
   */
  get areaGroups(): { label: string; items: BookableArea[] }[] {
    const byBranch = new Map<string, BookableArea[]>()
    for (const area of this.areas) {
      const label = area.event_name || this.translate.instant('BOOKINGS.FILTERS.UNKNOWN_BRANCH')
      const bucket = byBranch.get(label)
      if (bucket) bucket.push(area)
      else byBranch.set(label, [area])
    }
    return [...byBranch.entries()].sort((a, b) => a[0].localeCompare(b[0])).map(([label, items]) => ({ label, items }))
  }

  get hasMultipleBranches(): boolean {
    return this.areaGroups.length > 1
  }

  /** Room and booker are frozen once the booking exists — PAPI's update accepts times and title only. */
  get canChangeRoomAndPass(): boolean {
    return this.isNew
  }

  private buildForm(): FormGroup {
    return this.fb.group({
      area_id: [null, Validators.required],
      pass_id: [null, Validators.required],
      start: [null, Validators.required],
      end: [null, Validators.required],
      title: [''],
    })
  }

  /**
   * The add-a-person row. `setDefaultCodeByClient` on the shared control fills country_code, so it
   * starts empty here.
   *
   * The duplicate check rides on `valueChanges` rather than a `(change)` binding on the shared
   * control: that component exposes no such output, so the binding would fall through to the native
   * DOM event and depend on which of its two inner widgets happened to emit one. Re-subscribed on
   * every rebuild, since a fresh FormGroup carries none of the old one's subscriptions.
   */
  private buildInviteeForm(): FormGroup {
    const group = this.fb.group({
      country_code: [null, Validators.required],
      mobile: [null, Validators.required],
      name: [''],
    })
    group.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.onInviteeChange())
    return group
  }

  /**
   * `prefill` is the calendar handing over what the click on an empty slot already decided — the
   * room, the day and the hour. Only ever passed for a NEW booking; an existing one carries its own.
   */
  open(booking: Booking | null, areas: BookableArea[], prefill?: { area_id: number; start: Date; end: Date }): void {
    this.booking = booking
    this.areas = areas
    this.busyRanges = []
    this.candidates = []
    this.selectedCandidate = null
    this.lockedBookerName = ''
    this.form = this.buildForm()
    this.inviteeForm = this.buildInviteeForm()
    this.invitees = booking?.invitees ? booking.invitees.map((i) => ({ ...i })) : []
    this.inviteesTouched = false
    this.inviteeDuplicate = false

    if (booking) {
      this.form.patchValue({
        area_id: booking.area_id,
        pass_id: booking.pass_id,
        // The pickers show, and the admin edits, the CLIENT'S wall clock — the same one the grid
        // prints and PAPI validates against. `save()` converts back before anything is sent.
        start: this.bookingTime.toWall(booking.start_time),
        end: this.bookingTime.toWall(booking.end_time),
        title: booking.title ?? '',
      })
      this.form.get('area_id')?.disable()
      this.form.get('pass_id')?.disable()
      this.lockedBookerName = booking.pass_name || `#${booking.pass_id}`
    } else if (prefill) {
      this.form.patchValue({ area_id: prefill.area_id, start: prefill.start, end: prefill.end })
    } else {
      const start = this.nextSlot()
      this.form.patchValue({ start, end: new Date(start.getTime() + 60 * 60000) })
    }

    this.visible = true
    this.cdr.markForCheck()
  }

  /**
   * Default to the next whole half hour — the commonest slot size, and always a legal one.
   * On the client's clock, so a whole half hour there is a whole half hour in the picker too.
   */
  private nextSlot(): Date {
    const slot = 30 * 60000
    return new Date(Math.ceil((this.bookingTime.nowWall().getTime() + 5 * 60000) / slot) * slot)
  }

  close(): void {
    this.visible = false
    this.cdr.markForCheck()
  }

  get selectedArea(): BookableArea | undefined {
    const areaId = this.form.getRawValue()['area_id']
    return this.areas.find((area) => area.area_id === Number(areaId))
  }

  onAreaChange(): void {
    // Who may book is decided per ROOM (the role's area allow-list), and the candidates come from
    // the room's own branch — so a room change invalidates the person already chosen.
    this.form.get('pass_id')?.setValue(null)
    this.selectedCandidate = null
    this.candidates = []
    // The guest cap, the guest route and whether guests work at all are all per-room, so a list
    // built against the previous room is not a list this one would accept. Only reachable while
    // creating — the room is frozen on an existing booking.
    if (this.invitees.length) {
      this.invitees = []
      this.inviteesTouched = true
    }
    this.inviteeDuplicate = false
    this.cdr.markForCheck()
  }

  // ---------------------------------------------------------------------------------------------
  // Invitees (YB-2085)
  // ---------------------------------------------------------------------------------------------

  /** Whether to offer the section at all — both halves of the room's guest config must be set. */
  get guestsEnabled(): boolean {
    return !!this.selectedArea?.guests_enabled
  }

  /** Seats minus the booker. Zero for a room nobody has sized, which is a real answer, not a bug. */
  get maxInvitees(): number {
    return this.selectedArea?.max_invitees ?? 0
  }

  get inviteesFull(): boolean {
    return this.invitees.length >= this.maxInvitees
  }

  /**
   * Whether the list may be SENT back.
   *
   * PAPI matches invitees by phone number, and gives the portal the full number precisely so an
   * edit can round-trip. If an existing entry arrived without one — an older PAPI than this
   * portal — re-sending the list would miss every key and revoke the guest pass of everyone on it.
   * The list is then shown read-only instead: refusing to edit is recoverable, a mass revocation
   * dressed up as an edit is not.
   */
  get canEditInvitees(): boolean {
    return this.invitees.every((i) => !!i.mobile)
  }

  /** PAPI's `mobileKey`, verbatim — the identity of an invitee, and what a duplicate means here. */
  private inviteeKey(countryCode: string | null | undefined, mobile: string | null | undefined): string {
    const cc = String(countryCode ?? '').replace(/\D/g, '')
    const num = String(mobile ?? '')
      .replace(/\D/g, '')
      .replace(/^0+/, '')
    return `${cc}-${num}`
  }

  /** Re-checked as the admin types, so the Add button explains itself before it is pressed. */
  onInviteeChange(): void {
    const raw = this.inviteeForm.getRawValue()
    const key = this.inviteeKey(raw['country_code'], raw['mobile'])
    // `'-'` is the key of an empty row; without this an untouched form reads as a duplicate of any
    // existing invitee the portal could not see the number of.
    this.inviteeDuplicate = key !== '-' && this.invitees.some((i) => this.inviteeKey(i.country_code, i.mobile) === key)
    this.cdr.markForCheck()
  }

  get canAddInvitee(): boolean {
    return this.inviteeForm.valid && !this.inviteeDuplicate && !this.inviteesFull && this.canEditInvitees
  }

  addInvitee(): void {
    if (!this.canAddInvitee) return
    const raw = this.inviteeForm.getRawValue()
    const mobile = String(raw['mobile'] ?? '').trim()
    const countryCode = String(raw['country_code'] ?? '').replace(/\D/g, '')
    const name = String(raw['name'] ?? '').trim()

    // `kind` is PAPI's answer, not the portal's: whether this person needs a pass minted or already
    // holds one is decided server-side on save. 'guest' is the honest placeholder for "not yet
    // classified" — the response overwrites it, and nothing here acts on it.
    this.invitees = [...this.invitees, { name: name || null, mobile, country_code: countryCode, mobile_hint: mobile.slice(-4), kind: 'guest', pass_id: null }]
    this.inviteesTouched = true
    this.clearInviteeRow()
    this.cdr.markForCheck()
  }

  /**
   * Empty the add row IN PLACE — never by swapping in a fresh FormGroup.
   *
   * The shared mobile control attaches its validators and fills the default country code in
   * `ngOnInit`, and its `ngOnChanges` reacts to `disabled` alone. Handing it a new group mid-edit
   * therefore leaves it validating the controls of the old one, and the new one never gets a
   * country code. The code is deliberately kept rather than cleared: consecutive invitees are
   * nearly always in the same country, and it is the one field the control cannot refill by itself.
   */
  private clearInviteeRow(): void {
    this.inviteeForm.get('mobile')?.reset(null)
    this.inviteeForm.get('name')?.reset('')
    this.inviteeDuplicate = false
  }

  removeInvitee(index: number): void {
    if (!this.canEditInvitees) return
    this.invitees = this.invitees.filter((_, i) => i !== index)
    this.inviteesTouched = true
    this.onInviteeChange()
  }

  /** The list as PAPI takes it: full numbers, never the hint. */
  private inviteesPayload(): BookingInviteePayload[] {
    return this.invitees.map((i) => ({ mobile: String(i.mobile), country_code: String(i.country_code), name: i.name }))
  }

  /**
   * Who this room may be booked for, matched on name or phone.
   *
   * The search is entirely PAPI's: name and mobile are encrypted at rest, so a substring can only
   * be compared after decryption, which only the server can do. It also answers with the people
   * `create_booking` would actually accept for this room — booking-enabled, in range, allowed on
   * this area — so the picker no longer offers choices the save is going to refuse.
   */
  searchCandidates(event: { query: string }): void {
    const area = this.selectedArea
    const query = (event?.query ?? '').trim()
    if (!area || query.length < 2) {
      this.candidates = []
      return
    }

    this.candidatesLoading = true
    this.cdr.markForCheck()
    this.bookingsService
      .searchCandidates(area.area_id, query)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        this.candidates = result.success && Array.isArray(result.data) ? result.data : []
        this.candidatesLoading = false
        this.cdr.markForCheck()
      })
  }

  onCandidateSelect(event: { value: BookingCandidate }): void {
    this.selectedCandidate = event?.value ?? null
    this.form.get('pass_id')?.setValue(this.selectedCandidate ? this.selectedCandidate.pass_id : null)
    this.cdr.markForCheck()
  }

  onCandidateClear(): void {
    this.selectedCandidate = null
    this.form.get('pass_id')?.setValue(null)
    this.cdr.markForCheck()
  }

  /**
   * Angular can tell that both pickers are filled, but not that the second one is later than the
   * first. Without this the Create button looked enabled on a backwards range and then did
   * nothing at all when pressed, because `save()` returns early on exactly this condition.
   */
  get invalidRange(): boolean {
    const raw = this.form.getRawValue()
    const start: unknown = raw['start']
    const end: unknown = raw['end']
    if (!(start instanceof Date) || !(end instanceof Date)) return false
    return end.getTime() <= start.getTime()
  }

  /** The chosen length, as "1h 30m" — the number the room's min/max rules are judged against. */
  get durationLabel(): string {
    const raw = this.form.getRawValue()
    const start: unknown = raw['start']
    const end: unknown = raw['end']
    if (!(start instanceof Date) || !(end instanceof Date)) return ''
    const minutes = Math.round((end.getTime() - start.getTime()) / 60000)
    if (minutes <= 0) return ''
    const hours = Math.floor(minutes / 60)
    const rest = minutes % 60
    return [hours ? `${hours}h` : '', rest ? `${rest}m` : ''].filter(Boolean).join(' ')
  }

  save(): void {
    if (this.form.invalid || this.invalidRange || this.saving) return
    const raw = this.form.getRawValue()
    const startWall: Date = raw['start']
    const endWall: Date = raw['end']
    if (!(startWall instanceof Date) || !(endWall instanceof Date) || endWall.getTime() <= startWall.getTime()) return

    // Back to absolute UTC. The pickers hold the client's wall clock (see `open`), and the wire is
    // always UTC — sending the picker value raw would book the meeting at the browser's hour.
    const start = this.bookingTime.toUtc(startWall)
    const end = this.bookingTime.toUtc(endWall)

    this.saving = true
    this.busyRanges = []
    this.cdr.markForCheck()

    const done = (success: boolean, data?: unknown): void => {
      this.saving = false
      if (success) {
        this.saved.emit()
        this.close()
      } else {
        // booking_conflict carries the blocking ranges; the toast already named the refusal.
        const payload = data as { busy_ranges?: BookingBusyRange[] } | undefined
        this.busyRanges = payload?.busy_ranges ?? []
      }
      this.cdr.markForCheck()
    }

    // Captured before the callback: `this.booking` is a mutable property, so its narrowing does
    // not survive into the closure the confirm-and-retry may invoke twice.
    const editing = this.booking
    if (editing) {
      this.allowanceConfirm
        .runWithConfirmation((override) =>
          this.bookingsService.update(editing.id, {
            start_time: start.toISOString(),
            end_time: end.toISOString(),
            title: raw['title'] || null,
            // Only when the admin actually edited the list. Absent means "leave it alone"; an empty
            // array means "remove everyone", and PAPI revokes the guest passes accordingly — so
            // spreading this unconditionally would put a mass revocation behind a retitle.
            ...(this.inviteesTouched ? { invitees: this.inviteesPayload() } : {}),
            ...(override ? { override_allowance: true } : {}),
          })
        )
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((result) => done(result.success, result.data))
      return
    }

    // Minted ONCE, outside the attempt callback. The confirm-and-retry sends the same request a
    // second time, and a fresh key each time would turn the retry into a genuinely new booking —
    // which is exactly what idempotency_key exists to prevent.
    const idempotencyKey = this.newIdempotencyKey()

    this.allowanceConfirm
      .runWithConfirmation((override) =>
        this.bookingsService.create({
          event_id: Number(this.selectedArea?.event_id),
          area_id: Number(raw['area_id']),
          pass_id: Number(raw['pass_id']),
          start_time: start.toISOString(),
          end_time: end.toISOString(),
          title: raw['title'] || null,
          // Nothing to leave alone on a create, so the list is sent only when it has someone on it;
          // an empty array would be refused outright by a room that does not do guests.
          ...(this.invitees.length ? { invitees: this.inviteesPayload() } : {}),
          idempotency_key: idempotencyKey,
          ...(override ? { override_allowance: true } : {}),
        })
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => done(result.success, result.data))
  }

  private newIdempotencyKey(): string {
    const cryptoRef = globalThis.crypto as Crypto | undefined
    if (cryptoRef?.randomUUID) return cryptoRef.randomUUID()
    return `portal-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
  }
}
